import { httpInterceptor } from './http.interceptor';

export interface MultiNivelSummary {
  total_diamantes_mes: number;
  total_vendido_comprado_mes: number;
  valor_minimo_necessario: number;
  mes_referencia: string;
  role: string;
  atingiu_minimo: boolean;
}

export interface IndicadoNivel1 {
  primeiro_nome: string;
  primeira_letra_sobrenome: string;
  data_indicacao: string;
}

export interface Receipt {
  tipo: 'venda' | 'aprovacao' | 'primeira_venda';
  nivel: 1 | 2 | 3;
  diamantes_gerados: number;
  codigo_pagarme: string | null;
  codigo_vendedora: number;
  created_at: string;
}

export interface RankingIndicacao {
  posicao: number;
  nome: string;
  codigo: string;
  indicacoes_nivel_1: number;
}

export interface IndicanteResponse {
  success: boolean;
  data?: {
    nome: string;
  };
  message?: string;
}

export interface AdesaoResponse {
  success: boolean;
  data?: {
    id: number;
    user_id: number;
    status: string;
    date_created: string;
    observation?: string;
    codigo?: string;
    usuario?: {
      nome: string;
      role: string;
    };
  };
  message?: string;
}

export interface AdesaoRequest {
  user_id: number;
  observation?: string;
}

export interface RankingMensalResponse {
  mes: number;
  ano: number;
  periodo_inicio: string;
  periodo_fim: string;
  ranking: RankingIndicacao[];
}

export interface MultiNivelExtrato {
  id: number;
  usuario_id: number;
  mes_referencia: string; // formato YYYY-MM
  total_diamantes_nivel_1: number;
  total_diamantes_nivel_2: number;
  total_diamantes_nivel_3: number;
  total_diamantes_aprovacao: number;
  total_diamantes_geral: number;
  created_at: string;
  processado: boolean;
  minimo_atingido: boolean;
}

export interface IndicacoesResumo {
  total_nivel_1: number;
  total_nivel_2: number;
  total_nivel_3: number;
  total_geral: number;
  indicados_nivel_1: IndicadoNivel1[];
}

export interface IndicacoesDashboardData {
  total_clientes_indicados: number;
  total_revendedoras_indicadas: number;
  clientes_ativos: number;
  clientes_faltam_ativar: number;
  revendedoras_ativas: number;
  revendedoras_faltam_ativar: number;
  periodo: {
    inicio: string;
    fim: string;
  };
}

export interface NetworkNode {
  id: number;
  nome_razao_social: string;
  codigo: number | null;
  email: string | null;
  role: 'revendedora' | 'cliente';
  nivel: 0 | 1 | 2 | 3;
  totalVendas: number | null;
  totalDiamantes: number | null;
  metMinimoBatido: boolean;
  hasChildren: boolean;
  children?: NetworkNode[];
  dateIndicacao?: string;
}

export interface DirectChildDto {
  id: number;
  nome_razao_social: string;
  codigo: number | null;
  email: string | null;
  role: string;
  totalVendas: number | null;
  totalDiamantes: number | null;
  metMinimoBatido: boolean;
  hasChildren: boolean;
  dateIndicacao?: string;
}

export interface NetworkIndicacao {
  id: number;
  nome_razao_social: string;
  codigo: number | null;
  email: string | null;
  role: 'revendedora' | 'cliente';
  nivel: 1 | 2 | 3;
  totalVendas: number | null;
  totalDiamantes: number | null;
  metMinimoBatido: boolean;
}

export interface IndicadorDto {
  id: number;
  nome_razao_social: string;
  codigo: number;
  email: string | null;
  role: 'revendedora' | 'cliente';
  nivel: 1;
  totalVendas: number | null;
  totalDiamantes: number | null;
  metMinimoBatido: boolean;
  children: [];
}

export interface NetworkEstatisticas {
  totalVendasRede: number | null;
  totalDiamantesRede: number | null;
  totalIndicacoes: number;
  porNivel: {
    nivel1: number;
    nivel2: number;
    nivel3: number;
  };
}

export interface GetNetworkResponse {
  usuarioPrincipal: {
    id: number;
    nome_razao_social: string;
    codigo: number;
    email: string;
    role: string;
  };
  indicador: IndicadorDto | null;
  indicacoes: NetworkIndicacao[];
  estatisticas: NetworkEstatisticas;
  orgChartData?: NetworkNode; // deprecated — não retornado pela API
}

class MultiNivelService {
  /**
   * Busca dados sumarizados de multi-nível do usuário logado
   */
  async getSummary(): Promise<MultiNivelSummary> {
    try {
      const response = await httpInterceptor.get('/multi-nivel/summary');
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar summary de multi-nível:', error);
      throw error;
    }
  }

  /**
   * Busca dados sumarizados de multi-nível de um usuário específico
   */
  async getSummaryByUserId(usuarioId: number): Promise<MultiNivelSummary> {
    try {
      const response = await httpInterceptor.get(`/multi-nivel/summary/${usuarioId}`);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar summary de multi-nível por ID:', error);
      throw error;
    }
  }

  /**
   * Busca resumo de indicações do usuário logado
   */
  async getIndicacoesResumo(): Promise<IndicacoesResumo> {
    try {
      const response = await httpInterceptor.get('/multi-nivel/indicacoes-resumo');
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar indicações resumo:', error);
      throw error;
    }
  }

  /**
   * Busca resumo de indicações de um usuário específico
   */
  async getIndicacoesResumoByUserId(usuarioId: number): Promise<IndicacoesResumo> {
    try {
      const response = await httpInterceptor.get(`/multi-nivel/indicacoes-resumo/${usuarioId}`);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar indicações resumo por ID:', error);
      throw error;
    }
  }

  /**
   * Busca receipts do mês atual do usuário logado
   */
  async getReceiptsMesAtual(): Promise<Receipt[]> {
    try {
      const response = await httpInterceptor.get('/multi-nivel/receipts-mes-atual');
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar receipts do mês atual:', error);
      throw error;
    }
  }

  /**
   * Busca receipts do mês atual de um usuário específico
   */
  async getReceiptsMesAtualByUserId(usuarioId: number): Promise<Receipt[]> {
    try {
      const response = await httpInterceptor.get(`/multi-nivel/receipts-mes-atual/${usuarioId}`);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar receipts do mês atual por ID:', error);
      throw error;
    }
  }

  /**
   * Busca o ranking mensal atual de indicações
   */
  async getRankingMensalAtual(): Promise<RankingMensalResponse> {
    try {
      const response = await httpInterceptor.get('/client/ranking-mensal');
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar ranking mensal atual:', error);
      throw error;
    }
  }

  /**
   * Busca o ranking mensal de um período específico
   */
  async getRankingMensalPorPeriodo(ano: number, mes: number): Promise<RankingMensalResponse> {
    try {
      const response = await httpInterceptor.get(`/client/ranking-mensal/${ano}/${mes}`);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar ranking mensal por período:', error);
      throw error;
    }
  }

  /**
   * Busca ranking detalhado do mês atual para um indicador
   */
  async getRankingDetalhado(indicadoraId: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/client/ranking-mensal-detalhado/${indicadoraId}`);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar ranking detalhado:', error);
      throw error;
    }
  }

  /**
   * Busca ranking detalhado para um mês específico
   */
  async getRankingDetalhadoPorPeriodo(indicadoraId: number, ano: number, mes: number): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/client/ranking-mensal-detalhado/${indicadoraId}/${ano}/${mes}`);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar ranking detalhado por período:', error);
      throw error;
    }
  }

  /**
   * Busca indicante por código
   */
  async getIndicantePorCodigo(codigo: string): Promise<IndicanteResponse> {
    try {
      const response = await httpInterceptor.get(`/multi-nivel/indicante/${codigo}`);
      
      const data = await response.json();
      
      // A resposta da API vem em formato: { statusCode: 200, data: { success: true, data: { nome: "..." } } }
      if (response.ok && data.data && data.data.success) {
        return {
          success: true,
          data: data.data.data
        };
      } else {
        return {
          success: false,
          message: data.data?.message || 'Código de indicação não encontrado'
        };
      }
    } catch (error) {
      console.error('Erro ao buscar indicante por código:', error);
      return {
        success: false,
        message: 'Erro ao verificar código de indicação'
      };
    }
  }

  /**
   * Verifica adesão de um usuário
   */
  async getAdesaoByUserId(userId: number): Promise<AdesaoResponse> {
    try {
      const response = await httpInterceptor.get(`/multi-nivel/adesao/${userId}`);
      
      const data = await response.json();
      
      if (response.ok && data.data && data.data.success) {
        return {
          success: true,
          data: data.data.data
        };
      } else {
        return {
          success: false,
          message: data.data?.message || 'Adesão não encontrada'
        };
      }
    } catch (error) {
      console.error('Erro ao buscar adesão:', error);
      return {
        success: false,
        message: 'Erro ao verificar adesão'
      };
    }
  }

  /**
   * Cria uma nova adesão
   */
  async criarAdesao(adesaoData: AdesaoRequest): Promise<AdesaoResponse> {
    try {
      const response = await httpInterceptor.post('/multi-nivel/adesao', adesaoData);
      
      const data = await response.json();
      
      if (response.ok && data.data && data.data.success) {
        return {
          success: true,
          data: data.data.data,
          message: data.data.message
        };
      } else {
        return {
          success: false,
          message: data.data?.message || 'Erro ao criar adesão'
        };
      }
    } catch (error) {
      console.error('Erro ao criar adesão:', error);
      return {
        success: false,
        message: 'Erro ao criar adesão'
      };
    }
  }

  /**
   * Busca extratos de multi-nível por usuário
   */
  async getExtratosByUserId(usuarioId: number): Promise<MultiNivelExtrato[]> {
    try {
      const response = await httpInterceptor.get(`/multi-nivel/extratos/${usuarioId}`);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar extratos de multi-nível:', error);
      throw error;
    }
  }

  /**
   * Busca dashboard de indicações com filtro de período
   */
  async getIndicacoesDashboard(startDate?: string, endDate?: string): Promise<IndicacoesDashboardData> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await httpInterceptor.get(`/multi-nivel/indicacoes-dashboard?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar dashboard de indicações:', error);
      throw error;
    }
  }

  /**
   * Cria/altera indicação manual (remanejamento de indicador)
   */
  async criarIndicacaoManual(indicacoes: Array<{ codigo_indicante: number; codigo_indicada: number }>): Promise<any> {
    try {
      const response = await httpInterceptor.post('/multi-nivel/manual-indication-mmn', { indicacoes });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'Erro ao atribuir indicador');
      }

      return data;
    } catch (error) {
      console.error('Erro ao criar indicação manual:', error);
      throw error;
    }
  }

  /**
   * Busca a estrutura completa da rede de indicação de um usuário
   */
  async getNetworkStructure(usuarioId: number): Promise<GetNetworkResponse> {
    try {
      const response = await httpInterceptor.get(`/multi-nivel/network/${usuarioId}`);
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar estrutura da rede:', error);
      throw error;
    }
  }

  /**
   * Busca os filhos diretos (nível 1) de um usuário na rede.
   * Usado pelo lazy loading no organograma e visão gráfica.
   */
  async getDirectChildren(usuarioId: number): Promise<DirectChildDto[]> {
    try {
      const response = await httpInterceptor.get(`/multi-nivel/network/${usuarioId}/children`);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erro ao buscar filhos da rede:', error);
      throw error;
    }
  }
}

export const multiNivelService = new MultiNivelService();
